import { useEffect, useCallback, useState } from "react";
import styled from "styled-components";
import { IoCheckmarkDoneCircleOutline, IoChevronForward } from "react-icons/io5";
import { useNavigate } from 'react-router-dom';
import { APP_NAME } from '../constants/app';
import { useTranslation } from 'react-i18next';
import useUserStore from '../stores/userStore';
import useDailyRewardStore from '../stores/dailyRewardStore';
import { useAdsgram } from '../hooks/useAdsgram';
import WebApp from '@twa-dev/sdk';
import { toast } from 'react-toastify';
import { apiService } from '../services/api';
import DailyRewardModal from './DailyRewardModal';
import { FaFire } from 'react-icons/fa';
import CurrencyIcon from './CurrencyIcon';

const ADSGRAM_BLOCK_ID = '10786'; // замените на ваш реальный blockId

function Home() {
	const user = useUserStore(state => state.user);
	const balance = user?.balance ? parseFloat(user.balance) : 0;
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { fetchStreak, setStreak } = useDailyRewardStore();
	const [subscriptionLoading, setSubscriptionLoading] = useState(false);
	const [showSubscribeButton, setShowSubscribeButton] = useState(false);
	const fetchUser = useUserStore(state => state.fetchUser);
	const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
	const streak = useDailyRewardStore(state => state.streak);

	useEffect(() => {
		console.log('Home баланс:', balance);
	}, [balance]);

	useEffect(() => {
		console.log('Home user изменился:', user);
	}, [user]);

	useEffect(() => {
		console.log('Home component mounted');
		if (user) {
			console.log('Fetching streak for user:', user);
			fetchStreak();
		}
	}, [fetchStreak, user]);

	const handleBuyTokens = () => {
		navigate('/packages');
	};

	const onReward = useCallback(() => {
		toast.success(t('common.rewardReceived'));
	}, [t]);

	const onError = useCallback((result) => {
		toast.error(t('common.adError', { error: JSON.stringify(result, null, 2) }));
	}, [t]);

	const showAd = useAdsgram({ blockId: ADSGRAM_BLOCK_ID, onReward, onError });

	const getBotUsername = () => {
		return process.env.REACT_APP_BOT_USERNAME || '';
	};
	const handleShareReferral = () => {
		const botUsername = getBotUsername();
		const refLink = `https://t.me/${botUsername}?startapp=ref_${user.ref || ''}`;

		if (WebApp.isVersionAtLeast('6.0')) {
			WebApp.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(refLink)}&text=${encodeURIComponent(t('common.shareReferral'))}`);
		} else {
			navigator.clipboard.writeText(refLink);
			toast.info(t('common.linkCopied'));
		}
	};

	const handleDailyClaim = async () => {
		try {
			await fetchStreak();
			toast.success(t('tasks.daily_claimed'));
		} catch (error) {
			toast.error(t('common.error'));
		}
	};

	const handleSubscribe = () => {
		const channelUsername = process.env.REACT_APP_CHANNEL_USERNAME || 'skinforge';
		console.log('channelUsername', channelUsername);
		const channelUrl = `https://t.me/${channelUsername}`;

		// Открываем канал в новом окне
		window.open(channelUrl, '_blank');

		// Показываем кнопку проверки через 5 секунд
		setTimeout(() => {
			setShowSubscribeButton(true);
		}, 1000);
	};

	const handleCheckSubscription = async () => {
		try {
			setSubscriptionLoading(true);
			await apiService.checkSubscription();
			await fetchUser();
			setShowSubscribeButton(false);
			toast.success(t('tasks.subscription_checked'));
		} catch (error) {
			toast.error(t('common.error'));
			setShowSubscribeButton(false);
		} finally {
			setSubscriptionLoading(false);
		}
	};

	const handleRewardIconClick = () => {
		console.log('Reward icon clicked');
		console.log('Current streak:', streak);
		setIsRewardModalOpen(true);
	};

	return (
		<Container>
			<Content>
				<Header>
					<Title>{APP_NAME}</Title>
					<Subtitle>{t('tokens.description', { currency: t('currency_title') })}</Subtitle>
				</Header>

				<BalancePanel>
					<BalanceIconWrap>
						<CurrencyIcon size={72} />
					</BalanceIconWrap>
					<BalanceInfo>
						<BalanceLabel>{t('currency_title')} {t('main.balance')} </BalanceLabel>
						<BalanceValue> {balance ? balance.toLocaleString('ru-RU', { maximumFractionDigits: 2 }) : '0'}</BalanceValue>
					</BalanceInfo>
				</BalancePanel>

				<BuyButton onClick={handleBuyTokens}>
					{t('buyTokens', { currency: t('currency_title') })}
				</BuyButton>

				<TasksSection>
					<SectionTitle>{t('tasks.title')}</SectionTitle>
					<TasksList>
						{/* Ежедневный клейм */}
						<TaskCard onClick={handleRewardIconClick} style={{ cursor: 'pointer' }}>
							<TaskIcon>🎁</TaskIcon>
							<TaskInfo>
								<TaskDescription>{t('tasks.daily_claim')}</TaskDescription>
								<TaskReward>+100 <CurrencyIcon size={16} /></TaskReward>
							</TaskInfo>
							<TaskButton as="div" style={{
								pointerEvents: 'none',
								background: 'transparent',
								color: streak?.currentStreak === 7 ? '#ff9800' : '#2563eb',
								fontWeight: 600,
								display: 'flex',
								alignItems: 'center',
								gap: 6
							}}>
								{t('tasks.streak_label', { days: streak?.currentStreak || 0 })}
								{streak?.currentStreak === 7 && <FaFire color="#ff9800" style={{ marginLeft: 4 }} />}
							</TaskButton>
						</TaskCard>
						{/* Подписка */}
						<TaskCard>
							<TaskIcon>📢</TaskIcon>
							<TaskInfo>
								<TaskDescription>{t('tasks.subscribe', { app: APP_NAME })}</TaskDescription>
								<TaskReward>+100 <CurrencyIcon size={16} /></TaskReward>
							</TaskInfo>
							{subscriptionLoading ? (
								<TaskButton disabled>
									{t('common.loading')}
								</TaskButton>
							) : user?.tgSubscribed ? (
								<IoCheckmarkDoneCircleOutline size={36} color="green" />
							) : showSubscribeButton ? (
								<TaskButton onClick={handleCheckSubscription}>
									{t('tasks.check')}
									<IoChevronForward size={16} />
								</TaskButton>
							) : (
								<TaskButton onClick={handleSubscribe}>
									{t('tasks.subscribe_action')}
									<IoChevronForward size={16} />
								</TaskButton>
							)}
						</TaskCard>
						{/* Приглашение друга */}
						<TaskCard>
							<TaskIcon>🙋‍♂️</TaskIcon>
							<TaskInfo>
								<TaskDescription>{t('tasks.invite_friend')}</TaskDescription>
								<TaskReward>+500 <CurrencyIcon size={16} /></TaskReward>
							</TaskInfo>
							<TaskButton onClick={handleShareReferral}>
								{t('tasks.invite_action')}
								<IoChevronForward size={16} />
							</TaskButton>
						</TaskCard>
						{/* Открыть первый кейс */}
						<TaskCard>
							<TaskIcon>📦</TaskIcon>
							<TaskInfo>
								<TaskDescription>{t('tasks.open_first_case')}</TaskDescription>
								<TaskReward>+100 <CurrencyIcon size={16} /></TaskReward>
							</TaskInfo>
							{user && !user.caseOpened ? (
								<TaskButton onClick={() => navigate('/cases')}>
									{t('tasks.open_action')}
									<IoChevronForward size={16} />
								</TaskButton>
							) : (
								<IoCheckmarkDoneCircleOutline size={36} color="green" />
							)}
						</TaskCard>
						{/* Смотреть рекламу */}
						{/* <TaskCard>
							<TaskIcon>📺</TaskIcon>
							<TaskInfo>
								<TaskDescription>Смотреть рекламу</TaskDescription>
								<TaskReward>+20 <CurrencyIcon size={16} /></TaskReward>
							</TaskInfo>
							<TaskButton onClick={showAd}>
								Открыть
								<IoChevronForward size={16} />
							</TaskButton>
						</TaskCard> */}

					</TasksList>
				</TasksSection>
			</Content>
			<DailyRewardModal
				isOpen={isRewardModalOpen}
				onClose={() => setIsRewardModalOpen(false)}
			/>
		</Container>
	);
}

const Container = styled.div`
	min-height: 100vh;
	background: ${props => props.theme.background};
	padding: 0 16px;
	box-sizing: border-box;
`;

const Content = styled.div`
	${'' /* margin-top: 32px; */}
`;

const Header = styled.div`
	text-align: center;
	margin-bottom: 32px;
`;

const Title = styled.h1`
	font-size: 32px;
	font-weight: 700;
	margin: 0 0 12px 0;
	color: ${props => props.theme.text.primary};
`;

const Subtitle = styled.p`
	font-size: 16px;
	line-height: 1.5;
	color: ${props => props.theme.text.secondary};
	max-width: 400px;
	margin: 0 auto;
`;

const BalancePanel = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${props => props.theme.balancePanel};
	border-radius: 20px;
	padding: 24px 32px;
	box-shadow: 0 2px 16px rgba(0,0,0,0.08);
	margin-bottom: 24px;
	gap: 32px;
	min-height: 120px;
	max-width: 600px;
	margin-left: auto;
	margin-right: auto;
	@media (max-width: 700px) {
		max-width: 98vw;
		padding: 16px 4vw;
		gap: 24px;
	}
`;

const BalanceIconWrap = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	background: radial-gradient(circle, #23272f 70%, #181c24 100%);
	box-shadow: 0 0 16px #FFD70044;
	${props => props.theme.type === 'light' ? 'filter: brightness(0.8);' : ''}
`;

const BalanceInfo = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
`;

const BalanceLabel = styled.div`
	color: ${props => props.theme.type === 'light' ? '#222' : 'rgb(153, 153, 153)'};
	font-size: 22px;
	font-weight: 700;
	margin-bottom: 8px;
	${'' /* text-shadow: 0 2px 8px #000a; */}
`;

const BalanceValue = styled.div`
	color: ${props => props.theme.type === 'light' ? '#111' : '#fff'};
	font-size: 32px;
	font-weight: 700;
	${'' /* text-shadow: 0 2px 8px #000a; */}
	line-height: 1.1;
`;

const BuyButton = styled.button`
	width: 100%;
	max-width: 600px;
	margin-left: auto;
	margin-right: auto;
	background: ${props => props.theme.primary};
	border: none;
	border-radius: 12px;
	padding: 16px;
	color: white;
	font-size: 16px;
	font-weight: 600;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	cursor: pointer;
	transition: opacity 0.2s;
	margin-bottom: 32px;

	&:hover {
		opacity: 0.9;
	}

	&:active {
		opacity: 0.8;
	}
`;

const TasksSection = styled.div`
	margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
	font-size: 20px;
	font-weight: 600;
	margin: 0 0 16px 0;
	color: ${props => props.theme.text.primary};
`;

const TasksList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const TaskCard = styled.div`
	background: ${props => props.theme.card.background};
	border-radius: 12px;
	padding: 16px;
	display: flex;
	align-items: center;
	gap: 16px;
	box-shadow: ${props => props.theme.card.shadow};
`;

const TaskIcon = styled.div`
	font-size: 24px;
	min-width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${props => props.theme.background};
	border-radius: 8px;
`;

const TaskInfo = styled.div`
	flex: 1;
`;

const TaskDescription = styled.div`
	font-size: 14px;
	margin-bottom: 4px;
	color: ${props => props.theme.text.primary};
`;

const TaskReward = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 12px;
	color: ${props => props.theme.text.secondary};
`;

const TaskButton = styled.button`
	background: none;
	border: none;
	color: ${props => props.theme.primary};
	font-size: 14px;
	font-weight: 500;
	display: flex;
	align-items: center;ы
	gap: 4px;
	cursor: pointer;
	padding: 8px;
	border-radius: 8px;
	transition: background 0.2s;

	&:hover {
		background: ${props => props.theme.background};
	}
`;

export default Home;